export type ResolverType =
    | 'ADDRESS_PART'
    | 'CHARACTER'
    | 'FIELD'
    | 'GEOFENCE'
    | 'HASH'
    | 'INTELLIGENCE'
    | 'MEASUREMENT'
    | 'NAME_PART'
    | 'NUMERIC'
    | 'REGEX'
    | 'TEMPORAL'
    | 'WORD';

interface ResolverBase {
    uid: string;
    type: ResolverType;
    explicit: boolean;
    weight: number;
}

export interface AddressPartResolver extends ResolverBase {
    type: 'ADDRESS_PART';
    streetNumber: string;
    streetName: string;
    aptSuite: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
}

export interface CharacterResolver extends ResolverBase {
    type: 'CHARACTER';
    operator: string;       // CONTAINS | STARTS | ENDS
    value: string;
}

export interface FieldResolver extends ResolverBase {
    type: 'FIELD';
    fieldName: string;
    operator: string;       // EXACT | CONTAINS | STARTS | ENDS
    fieldValue: string;
}

export interface GeoFenceResolver extends ResolverBase {
    type: 'GEOFENCE';
    swLatitude: number;
    swLongitude: number;
    neLatitude: number;
    neLongitude: number;
}

export interface HashResolver extends ResolverBase {
    type: 'HASH';
    hashType: string;       // MD5 | SHA1 | SHA256 | SHA512
    hashValue: string;
}

export interface IntelligenceResolver extends ResolverBase {
    type: 'INTELLIGENCE';
    intelType: string;
    definition: string;
    display: string;
    matchHash: string;
}

export interface MeasurementResolver extends ResolverBase {
    type: 'MEASUREMENT';
    measurementType: string;
    unit: string;
    value: number;
    precision: number;
}

export interface NamePartResolver extends ResolverBase {
    type: 'NAME_PART';
    prefix: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    shortName: string;
    nickname: string;
}

export interface NumericResolver extends ResolverBase {
    type: 'NUMERIC';
    value: number;
    precision: number;
    enforceType: boolean;
    numericType: string;
}

export interface RegExResolver extends ResolverBase {
    type: 'REGEX';
    pattern: string;
}

export interface TemporalResolver extends ResolverBase {
    type: 'TEMPORAL';
    dateType: string;       // DATE | DATETIME
    dateRange: string;      // SPECIFIC | RANGE
    fromMonth: string;
    fromDay: string;
    fromYear: string;
    fromHour: string;
    fromMinute: string;
    fromAP: string;         // AM | PM
    toMonth: string;
    toDay: string;
    toYear: string;
    toHour: string;
    toMinute: string;
    toAP: string;           // AM | PM
}

export interface WordResolver extends ResolverBase {
    type: 'WORD';
    operator: string;       // WORD | PHRASE | CONTAINS
    value: string;
    inputLang: string;
    alphaMatch: boolean;
    charMatch: boolean;
    translate: boolean;
    misspelled: boolean;
    acronym: boolean;
    abbreviation: boolean;
    containsType: string;   // ANY | ALL | MIN
    containsMin: number;
    containsDistance: string; // ANY | MAX
    containsDistanceMax: number;
}

export type Resolver =
    | AddressPartResolver
    | CharacterResolver
    | FieldResolver
    | GeoFenceResolver
    | HashResolver
    | IntelligenceResolver
    | MeasurementResolver
    | NamePartResolver
    | NumericResolver
    | RegExResolver
    | TemporalResolver
    | WordResolver;

export const RESOLVER_TYPE_META: Record<ResolverType, { displayName: string; icon: string }> = {
    ADDRESS_PART: { displayName: 'Address Part', icon: '/images/intel/place.svg' },
    CHARACTER: { displayName: 'Character', icon: '/images/intel/identifier.svg' },
    FIELD: { displayName: 'Field', icon: '/images/intel/metadata.svg' },
    GEOFENCE: { displayName: 'Geo-Fence', icon: '/images/intel/coordinate.svg' },
    HASH: { displayName: 'Hash Value', icon: '/images/intel/credential.svg' },
    INTELLIGENCE: { displayName: 'Intelligence', icon: '/images/intel/selector.svg' },
    MEASUREMENT: { displayName: 'Measurement', icon: '/images/intel/unit.svg' },
    NAME_PART: { displayName: 'Name Part', icon: '/images/intel/name.svg' },
    NUMERIC: { displayName: 'Numeric', icon: '/images/intel/attribute.svg' },
    REGEX: { displayName: 'Regular Expression', icon: '/images/intel/process.svg' },
    TEMPORAL: { displayName: 'Temporal', icon: '/images/intel/event.svg' },
    WORD: { displayName: 'Word', icon: '/images/intel/concept.svg' },
};
